import { TrashIcon } from '@heroicons/react/24/outline'
import { ReactNode } from 'react'
import { InfoIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'

export type SectionTableColumn<T> = {
  header: string
  /** Shown in a tooltip from an info icon beside the header. */
  headerInfo?: string
  width?: string
  center?: boolean
  render: (item: T, index: number) => ReactNode
  /** Tints this cell as an unsaved change. */
  changed?: (item: T, index: number) => boolean
}

export type SectionTableHeaderButton = {
  label: string
  onClick: () => void
  testId?: string
  variant?: `primary` | `secondary`
  icon?: React.ElementType
}

type SectionTableProps<T> = {
  title: string
  icon?: ReactNode
  columns: SectionTableColumn<T>[]
  data: T[]
  rowKey: (item: T, index: number) => string | number
  headerButtons?: SectionTableHeaderButton[]
  highlightedRows?: Set<number>
  emptyMessage?: string
  testId?: string
  /** Draws the title as the card's own header row, inside the border, instead of above the table. */
  attached?: boolean
  /** Sits under the last row, e.g. an Add button where the new row will appear. */
  footer?: ReactNode
  /** Tints the whole row as an unsaved addition. */
  isNewRow?: (item: T, index: number) => boolean
}

export function SectionTable<T>({
  title,
  icon,
  columns,
  data,
  rowKey,
  headerButtons,
  highlightedRows,
  emptyMessage = `No items configured.`,
  testId,
  attached = false,
  footer,
  isNewRow,
}: SectionTableProps<T>) {
  return (
    <div className={attached ? `flex flex-col overflow-hidden rounded-lg border border-default` : `flex flex-col`}>
      <div
        className={
          attached
            ? `flex items-center justify-between gap-4 border-b border-default bg-surface px-4 py-3`
            : `flex items-center justify-between px-1 pb-4`
        }
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-body-md font-semibold">{title}</span>
        </div>
        {headerButtons && headerButtons.length > 0 && (
          <div className="flex gap-2">
            {headerButtons.map((btn) => {
              const Icon = btn.icon
              return (
                <Button
                  key={btn.label}
                  type="button"
                  variant={btn.variant === `secondary` ? `white` : `secondary`}
                  size="sm"
                  data-testid={btn.testId}
                  onClick={btn.onClick}
                >
                  {Icon && <Icon />}
                  {btn.label}
                </Button>
              )
            })}
          </div>
        )}
      </div>
      {/* Clips to the rounded border: the header row's fill otherwise paints square corners over
          it. Scrolls sideways inside the card when the columns outgrow a narrow page. */}
      <div className={attached ? `overflow-x-auto` : `overflow-x-auto rounded-lg border border-default`}>
        <table data-testid={testId} className="w-full">
          <thead>
            <tr className="border-b border-default bg-surface-subtle">
              {columns.map((col, colIdx) => (
                <th
                  key={col.header}
                  className={`${col.width ? col.width : ``} ${col.center ? `text-center` : `text-left`} ${
                    colIdx > 0 ? `border-l border-default` : ``
                  } px-4 py-4 text-label-sm text-default`}
                >
                  {col.headerInfo ? (
                    <span className="inline-flex items-center gap-1.5">
                      {col.header}
                      <TooltipWrapper message={col.headerInfo} placement="bottom-start" tooltipClassName="max-w-xs">
                        <span className="flex cursor-help items-center text-subtle" aria-label={`About ${col.header}`}>
                          <InfoIcon className="size-4" />
                        </span>
                      </TooltipWrapper>
                    </span>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={rowKey(item, index)}
                className={`border-b border-default last:border-b-0 ${
                  isNewRow?.(item, index)
                    ? `bg-warning-minimal`
                    : highlightedRows?.has(index)
                      ? `bg-primary-alpha-8`
                      : ``
                }`}
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.header}
                    className={`${col.center ? `text-center` : ``} ${
                      colIdx > 0 ? `border-l border-default` : ``
                    } ${col.changed?.(item, index) ? `bg-warning-minimal` : ``} px-4 py-4`}
                  >
                    {col.render(item, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && <div className="px-4 py-8 text-center text-ui-sm text-minimal">{emptyMessage}</div>}
      </div>
      {footer && <div className="border-t border-default">{footer}</div>}
    </div>
  )
}

export function SectionTableTrashButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center [&_svg]:text-default hover:[&_svg]:text-strong"
    >
      <TrashIcon className="h-4 w-4" />
    </button>
  )
}
